import { getPlanTerms, type LineupPlanId } from '@highlight/common'

// The "Limits reached" copy sheet, as one source for the banners, the blurred
// pages and chat. Pure, so every state is testable.

/** An allowance that runs out for the month. */
export type MonthlyLimit = 'credits' | 'hours' | 'briefs'

export interface LimitCopy {
  title: string
  body: string
  cta: string
}

export interface LimitCopyContext {
  /** The plan the user is on. */
  plan: LineupPlanId
  /** The plan Free is sold. Unused on a paid plan, which buys more instead. */
  upgradeTo: LineupPlanId
  /** When the allowances reset, as "Nov 1". */
  resets: string
}

export const formatCount = (n: number) => n.toLocaleString('en-US')

function termsOf(plan: LineupPlanId) {
  const terms = getPlanTerms(plan, 'v2-revamp')
  return {
    name: terms.label,
    credits: formatCount(terms.monthlyCredits),
    hours: terms.monthlyMeetingHours === null ? 'unlimited' : formatCount(terms.monthlyMeetingHours),
    briefs: terms.monthlyMeetingBriefs === null ? null : formatCount(terms.monthlyMeetingBriefs),
  }
}

/** Free can't buy more, so it is sold the upgrade; a paid plan buys more of what ran out. */
const sellsUpgrade = (ctx: LimitCopyContext) => ctx.plan === 'free'

/**
 * "Nov 1": the day a period ending at `periodEnd` resets. Without one, the
 * first of next month, which is where the server counts a period without a
 * subscription to.
 */
export function resetDateLabel(periodEnd: string | null | undefined, now = new Date()): string {
  const parsed = periodEnd ? new Date(periodEnd) : null
  const date =
    parsed && !Number.isNaN(parsed.getTime())
      ? parsed
      : new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 1))
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' })
}

export function hoursOutCopy(ctx: LimitCopyContext): LimitCopy {
  const current = termsOf(ctx.plan)
  const lead = `You’ve used this month’s ${current.hours} hours, so new meetings won’t be transcribed until ${ctx.resets}.`
  if (sellsUpgrade(ctx)) {
    const target = termsOf(ctx.upgradeTo)
    return {
      title: 'Transcription limit reached',
      body: `${lead} Upgrade to ${target.name} for ${target.hours} hours a month.`,
      cta: `Upgrade to ${target.name}`,
    }
  }
  return {
    title: 'Transcription limit reached',
    body: `${lead} Buy more hours to keep transcribing.`,
    cta: 'Buy hours',
  }
}

export function creditsOutCopy(ctx: LimitCopyContext): LimitCopy {
  const current = termsOf(ctx.plan)
  if (sellsUpgrade(ctx)) {
    const target = termsOf(ctx.upgradeTo)
    return {
      title: 'Credit limit reached',
      body: `You’ve used this month’s ${current.credits} credits, so chat is paused until ${ctx.resets}. Upgrade to ${target.name} for ${target.credits} credits a month.`,
      cta: `Upgrade to ${target.name}`,
    }
  }
  return {
    title: 'Credit limit reached',
    body: `You’ve used this month’s ${current.credits} credits, so chat and skills are paused until ${ctx.resets}. Buy more credits to keep using them.`,
    cta: 'Buy credits',
  }
}

/** A chat turn refused for credits. Free reads as it does everywhere; a paid plan's reply is about chat. */
export function chatCreditsOutCopy(ctx: LimitCopyContext): LimitCopy {
  if (sellsUpgrade(ctx)) {
    return creditsOutCopy(ctx)
  }
  return {
    title: 'Credit limit reached',
    body: `Chat is paused until ${ctx.resets}. Buy more credits to keep chatting.`,
    cta: 'Buy credits',
  }
}

export function briefsOutCopy(ctx: LimitCopyContext): LimitCopy {
  const target = termsOf(ctx.upgradeTo)
  return {
    title: 'Meeting brief limit reached',
    body: `You’ve used this month’s ${termsOf('free').briefs} meeting briefs, so upcoming meetings won’t get one until ${ctx.resets}. Upgrade to ${target.name} for unlimited meeting briefs.`,
    cta: `Upgrade to ${target.name}`,
  }
}

export function briefsLeftCopy(left: number, limit: number, resets: string): Omit<LimitCopy, 'cta'> {
  return {
    title: `${left} of ${limit} meeting briefs left`,
    body: `You can generate ${left} more until ${resets}.`,
  }
}

export function followUpsOutCopy(limit: number, ctx: LimitCopyContext): LimitCopy {
  const target = termsOf(ctx.upgradeTo)
  return {
    title: 'Follow-up limit reached',
    body: `You’ve used this month’s ${limit} follow-ups. You’ll still see the follow-ups Highlight finds, but can’t generate new ones until ${ctx.resets}. Upgrade to ${target.name} for follow-ups on every meeting.`,
    cta: `Upgrade to ${target.name}`,
  }
}

export function followUpsLeftCopy(left: number, limit: number, resets: string): Omit<LimitCopy, 'cta'> {
  return {
    title: `${left} of ${limit} follow-ups left`,
    body: `Highlight found a follow-up. You can generate ${left} more until ${resets}.`,
  }
}

/** "3:48 PM" is when the hours ran out; transcription carried on so the meeting is whole. */
export function ranOutMidMeetingCopy(ranOutAt: string, ctx: LimitCopyContext): LimitCopy {
  const lead = 'We kept transcribing so you could finish the meeting.'
  if (sellsUpgrade(ctx)) {
    const target = termsOf(ctx.upgradeTo)
    return {
      title: `Transcription limit reached at ${ranOutAt}`,
      body: `${lead} Upgrade to ${target.name} for ${target.hours} hours a month.`,
      cta: `Upgrade to ${target.name}`,
    }
  }
  return {
    title: `Transcription limit reached at ${ranOutAt}`,
    body: `${lead} Buy more hours to keep transcribing.`,
    cta: 'Buy hours',
  }
}

/** The order limits are named in, which the sheet keeps: credits, then hours, then briefs. */
const LIMIT_ORDER: ReadonlyArray<MonthlyLimit> = ['credits', 'hours', 'briefs']

/**
 * Two or three limits spent at once, as one banner. A paid plan has no brief
 * limit, so only Free can name one; its second way on (Buy hours) is the
 * caller's, from `secondaryCta`.
 */
export function combinedLimitsCopy(
  spent: ReadonlyArray<MonthlyLimit>,
  ctx: LimitCopyContext,
): LimitCopy & { secondaryCta?: string } {
  const has = new Set(spent)
  const { resets } = ctx
  if (!sellsUpgrade(ctx)) {
    return {
      title: 'Credit and transcription limits reached',
      body: `Chat and skills are paused and new meetings won’t be transcribed until ${resets}. Buy more credits and hours to keep going.`,
      cta: 'Buy credits',
      secondaryCta: 'Buy hours',
    }
  }
  const target = termsOf(ctx.upgradeTo)
  const cta = `Upgrade to ${target.name}`
  if (LIMIT_ORDER.every((limit) => has.has(limit))) {
    return {
      title: `${termsOf(ctx.plan).name} limits reached`,
      body: `Chat, transcription, and meeting briefs are paused until ${resets}. Upgrade to ${target.name} for ${target.credits} credits, ${target.hours} hours, and unlimited meeting briefs.`,
      cta,
    }
  }
  if (has.has('credits') && has.has('hours')) {
    return {
      title: 'Credit and transcription limits reached',
      body: `Chat is paused and new meetings won’t be transcribed until ${resets}. Upgrade to ${target.name} for ${target.credits} credits and ${target.hours} hours a month.`,
      cta,
    }
  }
  if (has.has('hours') && has.has('briefs')) {
    return {
      title: 'Transcription and meeting brief limits reached',
      body: `New meetings won’t be transcribed or get a meeting brief until ${resets}. Upgrade to ${target.name} for ${target.hours} hours a month and unlimited meeting briefs.`,
      cta,
    }
  }
  return {
    title: 'Credit and meeting brief limits reached',
    body: `Chat is paused and upcoming meetings won’t get a meeting brief until ${resets}. Upgrade to ${target.name} for ${target.credits} credits a month and unlimited meeting briefs.`,
    cta,
  }
}
